import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useActiveRole } from '@/features/auth/hooks/use-active-role';
import { useDataCache } from '@/store/data-cache-context';
import { UserRole } from '@/types/api';
import type { Building, Unit, CreatePropertyRequest, Gateway } from '@/types/api';
import { getPropertyGateways } from '@/features/gateways/api/gateways-api';
import { deviceDetailPath } from '@/features/devices/utils/device-navigation';
import { Building2, DoorOpen, Edit2, LayoutGrid, List, Plus, Search, Trash2, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Modal } from '@/components/ui/modal';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingPage } from '@/components/ui/loading';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { GoogleMap } from '@/components/ui/google-map';
import type { MapMarker } from '@/components/ui/google-map';
import { Pagination } from '@/components/ui/pagination';
import { PropertyStatStrip } from './property-stat-strip';
import { PropertyDeviceCard } from './property-device-card';
import { ReassignOrganizationModal } from './reassign-organization-modal';
import { SetupModeDeviceGrid } from './setup-mode-device-grid';
import { AssignDeviceModal } from './assign-device-modal';
import { PropertyModal } from './property-modal';
import { PropertyGatewaysTab } from './property-gateways-tab';
import { ExportDataDropdown } from './export-data-dropdown';
import { BulkUnitsForm } from './bulk-units-form';
import { bulkCreateUnitsInBuilding } from '@/features/buildings/api/buildings-api';
import { updateProperty, deleteProperty } from '../api/properties-api';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { TypeToConfirmDialog } from '@/components/ui/type-to-confirm-dialog';
import { Calendar } from 'lucide-react';
import { usePropertyPortal, DEVICE_PER_PAGE_OPTIONS } from '../hooks/use-property-portal';
import { useSetupModePolling } from '../hooks/use-setup-mode-polling';
import { createBuilding, updateBuilding, deleteBuilding } from '@/features/buildings/api/buildings-api';
import { createUnit, updateUnit, deleteUnit } from '@/features/units/api/units-api';
import { sortUnitsForDisplay } from '@/features/units/utils/sort-units-for-display';
import { UnitModal } from './unit-modal';
import { SetupModePanel } from './setup-mode-panel';
import { SetupConnectingPanel } from './setup-connecting-panel';
import type { ConnectingTarget } from '../utils/connecting-target';
import { PropertyDevicesTable } from './property-devices-table';
import { PropertyUnitsTab } from './property-units-tab';
import { useViewMode } from '../hooks/use-view-mode';
import { useMediaQuery } from '@/hooks/use-media-query';
import { cn } from '@/lib/utils';

// 24h freshness threshold for gateway "online" treatment in the stat
// strip. Mirrors gateway-detail-page.tsx and admin-gateways-page.tsx.
const GATEWAY_STALE_MS = 24 * 60 * 60 * 1000;

export function PropertyPortal() {
  const navigate = useNavigate();
  const activeRole = useActiveRole();
  const canWrite = activeRole === UserRole.Owner || activeRole === UserRole.Admin || activeRole === UserRole.Manager;
  const isSysAdmin = activeRole === UserRole.Owner;
  const [showReassignOrg, setShowReassignOrg] = useState(false);

  const {
    property,
    loading,
    buildings,
    units,
    devices,
    mapDevices,
    devicesLoading,
    devicesTotalCount,
    devicesTotalPages,
    devicesPage,
    setDevicesPage,
    devicesPerPage,
    setDevicesPerPage,
    devicesSortBy,
    setDevicesSortBy,
    devicesSortDir,
    setDevicesSortDir,
    selectedBuildingId,
    changeBuilding,
    selectedUnitId,
    setSelectedUnitId,
    refresh,
    isSetupMode,
    enterSetupMode,
    exitSetupMode,
  } = usePropertyPortal();
  const { invalidate } = useDataCache();
  // Tailwind `lg` breakpoint is 1024px — anything narrower is treated
  // as mobile / tablet. Desktop reads/writes the persisted preference;
  // mobile gets a session-only state that always starts on `grid` so
  // the card layout (which lays out much better on a narrow screen) is
  // the default every time the user lands. If they specifically toggle
  // to list while on mobile, that holds for the session only — the
  // next visit reverts to grid.
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [desktopViewMode, setDesktopViewMode] = useViewMode('norgas.propertyPortal.deviceViewMode', 'list');
  const [mobileViewMode, setMobileViewMode] = useState<'grid' | 'list'>('grid');
  const viewMode = isDesktop ? desktopViewMode : mobileViewMode;
  const setViewMode = isDesktop ? setDesktopViewMode : setMobileViewMode;

  const setupModeBuilding = isSetupMode
    ? buildings.find((b) => b.id === selectedBuildingId) ?? null
    : null;

  // While Setup Mode is active the polling hook is the source of truth —
  // the portal's paginated fetch isn't reactive to magnet transmissions.
  // Until the first poll response lands we keep showing the portal's list
  // so the table doesn't blank on the mode transition.
  const { devices: polledDevices, refetch: refetchPolled, hasLoaded: polledLoaded } = useSetupModePolling({
    enabled: isSetupMode,
    propertyId: property?.id,
    buildingId: selectedBuildingId,
    sortBy: devicesSortBy,
    sortDir: devicesSortDir,
  });
  const visibleDevices = isSetupMode && polledLoaded ? polledDevices : devices;

  // The device most recently set up this session — drives the prominent
  // "Connecting" animation until its magnet transmission lands. Cleared on
  // success/dismiss (by the panel) and whenever Setup Mode exits.
  const [connectingTarget, setConnectingTarget] = useState<ConnectingTarget | null>(null);
  useEffect(() => {
    if (!isSetupMode) setConnectingTarget(null);
  }, [isSetupMode]);

  // Setup Mode trusts the server's `sortBy=unit` order (UnitNumberComparer)
  // so the grid matches the property device list. Outside Setup Mode, server
  // order is preserved as well — no per-mode re-sort here.
  const orderedDevices = visibleDevices;

  // Active tab is mirrored to `?tab=` so deep links and refreshes preserve
  // it. `'units'` is the default (the canonical "what's in this property?"
  // view) and is represented by the param being absent, keeping URLs
  // clean. Existing emailed links with ?tab=devices still resolve.
  const [tabSearchParams, setTabSearchParams] = useSearchParams();
  const initialTabParam = tabSearchParams.get('tab');
  const [activeTab, setActiveTabState] = useState<'units' | 'devices' | 'gateways' | 'map'>(
    initialTabParam === 'devices' || initialTabParam === 'gateways' || initialTabParam === 'map'
      ? initialTabParam
      : 'units',
  );
  const setActiveTab = useCallback((tab: 'units' | 'devices' | 'gateways' | 'map') => {
    setActiveTabState(tab);
    setTabSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'units') next.delete('tab');
      else next.set('tab', tab);
      return next;
    }, { replace: true });
  }, [setTabSearchParams]);

  // (Removed the `?setup=1` auto-open useEffect — the QR scanner now
  // navigates directly to `?mode=setup&buildingId=...` so PropertyPortal
  // picks Setup Mode up via its existing `isSetupMode` derivation. The
  // previous indirection had a race where two back-to-back
  // setSearchParams calls each saw the same prev state, so the second
  // call's `delete('setup')` clobbered the first call's `set('mode', 'setup')`
  // and the user landed on the bare property page.)
  const [propertyGateways, setPropertyGateways] = useState<Gateway[]>([]);

  // Desktop sidebar building search. Buildings-only — units used to live
  // here too, but the May 14 batch redesign moved unit context onto the
  // device cards/rows instead. Keeping the sidebar (vs the filter
  // dropdown) preserves the desktop's "click a building → device list
  // filters" muscle memory.
  const [buildingSearch, setBuildingSearch] = useState('');

  // True when at least one of this property's gateways has reported
  // within the last 24h. Drives the green RadioTower in PropertyStatStrip.
  const hasOnlineGateway = useMemo(
    () => propertyGateways.some(
      (g) => g.lastSeenAt != null && (Date.now() - new Date(g.lastSeenAt).getTime()) < GATEWAY_STALE_MS,
    ),
    [propertyGateways],
  );

  // Stable marker array for the map. May 14 batch: device pins removed
  // per Tanner — meter density at property scope is noise. Property +
  // gateway pins remain (mirrors Dashboard map's shape). Rebuilding this
  // inline on every render caused the GoogleMap's markers-keyed effect
  // to re-init on unrelated state changes.
  const mapMarkers = useMemo(() => {
    const markers: MapMarker[] = [];
    if (property?.latitude != null && property?.longitude != null) {
      markers.push({ id: `property-${property.id}`, lat: property.latitude, lng: property.longitude, label: property.name, color: 'blue', icon: 'property' });
    }
    propertyGateways.filter((g) => g.latitude != null && g.longitude != null).forEach((g) => {
      markers.push({ id: `gateway-${g.id}`, lat: g.latitude!, lng: g.longitude!, label: g.name, color: 'slate', icon: 'gateway' });
    });
    return markers;
  }, [property, propertyGateways]);

  // Memoized center — wrap the inline object literal so re-renders don't
  // produce a new identity each time (matters even with the May 14
  // sticky-viewport split, since the init effect's deps benefit from
  // stable references).
  const mapCenter = useMemo(() => {
    return property?.latitude != null && property?.longitude != null
      ? { lat: property.latitude, lng: property.longitude }
      : undefined;
  }, [property?.latitude, property?.longitude]);
  const [showEditProperty, setShowEditProperty] = useState(false);
  const [confirmDeleteProperty, setConfirmDeleteProperty] = useState(false);
  const [deletePropertyLoading, setDeletePropertyLoading] = useState(false);

  // Edit building
  const [editingBuilding, setEditingBuilding] = useState<Building | null>(null);
  const [editBuildingName, setEditBuildingName] = useState('');
  const [editBuildingLoading, setEditBuildingLoading] = useState(false);

  // Edit unit
  const [editingUnit, setEditingUnit] = useState<Unit | null>(null);

  // Delete building/unit
  const [deletingBuilding, setDeletingBuilding] = useState<Building | null>(null);
  const [deletingUnit, setDeletingUnit] = useState<Unit | null>(null);

  // Assign Device modal — opens from the unit-empty empty state to bind
  // an unassigned device from the org's inventory to the selected unit.
  const [assignDeviceUnit, setAssignDeviceUnit] = useState<Unit | null>(null);

  // Add building modal
  const [showAddBuilding, setShowAddBuilding] = useState(false);
  const [newBuildingName, setNewBuildingName] = useState('');
  const [addBuildingError, setAddBuildingError] = useState('');
  const [addBuildingLoading, setAddBuildingLoading] = useState(false);

  // Add unit modal — single OR bulk. The toggle lives at the top of the
  // modal so installers don't have to back out to a different button to
  // get the bulk path. May 14 batch.
  const [showAddUnit, setShowAddUnit] = useState(false);
  const [addUnitMode, setAddUnitMode] = useState<'single' | 'bulk'>('single');
  const [newUnitNumber, setNewUnitNumber] = useState('');
  const [newUnitBuildingId, setNewUnitBuildingId] = useState('');
  const [addUnitError, setAddUnitError] = useState('');
  const [addUnitLoading, setAddUnitLoading] = useState(false);

  // Bulk-mode preview rows + server-error map. Pushed up by BulkUnitsForm
  // via onChange so the submit handler can read off the latest values.
  const [bulkPreviewUnits, setBulkPreviewUnits] = useState<{ unitNumber: string; deviceSerial?: string }[]>([]);
  const [bulkRowErrors, setBulkRowErrors] = useState<{ rowIndex: number; message: string }[] | undefined>(undefined);

  // Gateways for this property — drives the map and picks up the Gateways tab's data
  useEffect(() => {
    if (!property?.id) return;
    getPropertyGateways(property.id).then(setPropertyGateways).catch(() => setPropertyGateways([]));
  }, [property?.id]);

  // Re-fetch gateways every time the Map tab activates. Gateway locations
  // can change externally (e.g. user opens a gateway detail page in a side
  // navigation, edits its location, and comes back via browser back), and
  // the Map tab is the surface where stale coords look most wrong.
  useEffect(() => {
    if (activeTab !== 'map' || !property?.id) return;
    getPropertyGateways(property.id).then(setPropertyGateways).catch(() => { /* keep last list */ });
  }, [activeTab, property?.id]);

  // Units grouped by building for sidebar tree. Always sorted ascending
  // (numeric-prefix-first natural sort) so unit "1" shows before "10" and
  // creation order doesn't leak into the UI.
  const unitsByBuilding = useMemo(() => {
    const map: Record<string, Unit[]> = {};
    units.forEach((u) => { (map[u.buildingId] ??= []).push(u); });
    Object.keys(map).forEach((id) => { map[id] = sortUnitsForDisplay(map[id]); });
    return map;
  }, [units]);

  function openAddUnit(preselectedBuildingId?: string) {
    setNewUnitBuildingId(preselectedBuildingId ?? selectedBuildingId ?? '');
    setNewUnitNumber('');
    setAddUnitError('');
    setAddUnitMode('single');
    setBulkPreviewUnits([]);
    setBulkRowErrors(undefined);
    setShowAddUnit(true);
  }

  async function handleBulkAddUnits() {
    if (!newUnitBuildingId) {
      setAddUnitError('Pick a building first.');
      return;
    }
    const cleaned = bulkPreviewUnits
      .map((u) => ({ unitNumber: u.unitNumber.trim(), deviceSerial: u.deviceSerial?.trim() || undefined }))
      .filter((u) => u.unitNumber.length > 0);
    if (cleaned.length === 0) {
      setAddUnitError('Add at least one unit.');
      return;
    }
    setAddUnitError('');
    setAddUnitLoading(true);
    setBulkRowErrors(undefined);
    try {
      const res = await bulkCreateUnitsInBuilding(newUnitBuildingId, { units: cleaned });
      if (!res.ok && res.errorBody) {
        setBulkRowErrors(res.errorBody.errors.map((e) => ({ rowIndex: e.index, message: e.message })));
        setAddUnitError('Some rows have problems — see highlighted entries.');
        return;
      }
      invalidate(['units', 'buildings', 'properties']);
      setShowAddUnit(false);
      refresh();
    } catch (err: unknown) {
      setAddUnitError((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to create units.');
    } finally {
      setAddUnitLoading(false);
    }
  }

  async function handleAddBuilding() {
    if (!property || !newBuildingName.trim()) return;
    setAddBuildingError('');
    setAddBuildingLoading(true);
    try {
      const res = await createBuilding({ propertyId: property.id, name: newBuildingName.trim() });
      if (res.success) {
        invalidate(['buildings', 'properties']);
        setShowAddBuilding(false);
        setNewBuildingName('');
        refresh();
      } else {
        setAddBuildingError(res.message ?? 'Failed to create building.');
      }
    } catch (err: unknown) {
      setAddBuildingError((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to create building.');
    } finally {
      setAddBuildingLoading(false);
    }
  }

  async function handleAddUnit() {
    if (!newUnitNumber.trim() || !newUnitBuildingId) return;
    setAddUnitError('');
    setAddUnitLoading(true);
    try {
      const res = await createUnit({
        buildingId: newUnitBuildingId,
        unitNumber: newUnitNumber.trim(),
      });
      if (res.success) {
        invalidate(['units', 'properties']);
        setShowAddUnit(false);
        refresh();
      } else {
        setAddUnitError(res.message ?? 'Failed to create unit.');
      }
    } catch (err: unknown) {
      setAddUnitError((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to create unit.');
    } finally {
      setAddUnitLoading(false);
    }
  }

  async function handleDeleteProperty() {
    if (!property || deletePropertyLoading) return;
    setDeletePropertyLoading(true);
    try {
      await deleteProperty(property.id);
      // Invalidate before navigating so the Properties list page mounts
      // with a fresh fetch instead of showing the just-deleted row from
      // its page-keyed cache. Cascaded entities also drop their caches
      // since soft-deleting a property tombstones its buildings/units/devices.
      invalidate(['properties', 'buildings', 'units', 'devices']);
      navigate('/properties');
    } finally {
      setDeletePropertyLoading(false);
    }
  }

  async function handleEditBuilding() {
    if (!editingBuilding || !editBuildingName.trim()) return;
    setEditBuildingLoading(true);
    try {
      await updateBuilding(editingBuilding.id, { name: editBuildingName.trim() });
      invalidate(['buildings', 'properties', 'units']);
      setEditingBuilding(null);
      refresh();
    } catch { /* silent */ }
    finally { setEditBuildingLoading(false); }
  }

  async function handleDeleteBuilding() {
    if (!deletingBuilding) return;
    const b = deletingBuilding;
    setDeletingBuilding(null);
    await deleteBuilding(b.id);
    invalidate(['buildings', 'properties', 'units', 'devices']);
    if (selectedBuildingId === b.id) changeBuilding('');
    refresh();
  }

  async function handleDeleteUnit() {
    if (!deletingUnit) return;
    const u = deletingUnit;
    setDeletingUnit(null);
    setSelectedUnitId('');
    await deleteUnit(u.id);
    invalidate(['units', 'properties', 'devices']);
    refresh();
  }

  // Only show the blocking spinner on the first load. Subsequent refreshes
  // (refresh() after scans, edits, etc.) keep the page mounted so Setup Mode
  // state and scroll position aren't thrown away.
  if (loading && !property) return <LoadingPage />;

  if (!property) {
    return (
      <div className="p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <p className="text-gray-500 dark:text-gray-400">Property not found.</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/properties')}>Back to Properties</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">

        <Breadcrumb items={[
          { label: 'Properties', href: '/properties' },
          { label: property.name },
        ]} />

        {isSetupMode ? (
          <SetupModePanel
            building={setupModeBuilding}
            buildings={buildings}
            existingUnits={setupModeBuilding ? (unitsByBuilding[setupModeBuilding.id] ?? []) : []}
            onBuildingSelect={changeBuilding}
            onBuildingCreate={async (name) => {
              if (!property) return null;
              try {
                const res = await createBuilding({ propertyId: property.id, name });
                if (res.success && res.data?.id) {
                  invalidate(['buildings', 'properties']);
                  refresh();
                  return res.data.id;
                }
                return null;
              } catch {
                return null;
              }
            }}
            onExit={exitSetupMode}
            onScanned={(info) => { setConnectingTarget(info); void refetchPolled(); refresh(); }}
          />
        ) : (
          <>
            {/* Identity row — name, address, edit. Created date moved out of
                the header (it's metadata, not identity). */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">{property.name}</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{property.address}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {canWrite && <Button variant="outline" onClick={() => setShowEditProperty(true)}>Edit Property</Button>}
              </div>
            </div>

            {/* Stat strip — slim three-cell anchor between the identity row
                and the action toolbar. Replaces the deleted Buildings/Units/
                Meters card with stats that genuinely matter. */}
            <PropertyStatStrip
              meterCount={mapDevices.length}
              gatewayCount={propertyGateways.length}
              hasOnlineGateway={hasOnlineGateway}
              activeAlarmCount={property.activeAlarmCount}
              onAlarmClick={() => navigate(`/alerts?propertyId=${property.id}`)}
            />

            {/* Action buttons */}
            {/* Action toolbar — grouped left-to-right by intent:
                  Hero (Setup Mode) | Data ops (Schedule, Export) | Org ops (Add Building, Add Unit)
                Setup Mode is the page's hero CTA and gets a heavier weight
                + icon emphasis; secondary/tertiary actions stay outline. */}
            {canWrite && (
              <div className="flex items-center gap-2 flex-wrap">
                {!isSetupMode && (
                  <>
                    <Button
                      variant="primary"
                      onClick={enterSetupMode}
                      title="Scan meters into this property"
                      className="shadow-sm"
                    >
                      <Zap className="w-4 h-4" />
                      Setup Mode
                    </Button>
                    <span aria-hidden="true" className="hidden sm:block w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1" />
                  </>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/properties/${property.id}/scheduled-reports`)}
                >
                  <Calendar className="w-3.5 h-3.5" />
                  Scheduled Reports
                </Button>
                <ExportDataDropdown
                  propertyId={property.id}
                  sortBy={devicesSortBy}
                  sortDir={devicesSortDir}
                />
                <span aria-hidden="true" className="hidden sm:block w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1" />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setNewBuildingName(''); setAddBuildingError(''); setShowAddBuilding(true); }}
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Building
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openAddUnit()}
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Unit
                </Button>
                {isSysAdmin && (
                  <>
                    <span aria-hidden="true" className="hidden sm:block w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1" />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowReassignOrg(true)}
                      title="Move this property to a different organization (Sys Admin only)"
                    >
                      Reassign Org
                    </Button>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* Two-pane layout: building sidebar (desktop) + tabs/content.
            Mobile collapses the sidebar to a single Building <Select>
            inline at the top of the right pane. Units no longer live in
            the sidebar tree (May 14 redesign) — unit context surfaces on
            each device card/row instead. */}
        {(() => {
          const filteredBuildings = buildingSearch.trim()
            ? buildings.filter((b) => b.name.toLowerCase().includes(buildingSearch.toLowerCase()))
            : buildings;
          return (
        <div className="flex items-start gap-6">
          {/* Desktop sidebar — hidden during Setup Mode so the card grid
              uses the full width (the Setup Mode panel has its own
              building picker / Change link). */}
          <div className={cn(
            'hidden lg:block w-64 flex-shrink-0 sticky top-4',
            isSetupMode && 'lg:hidden',
          )}>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search buildings..."
                    value={buildingSearch}
                    onChange={(e) => setBuildingSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>
              <nav className="p-2 space-y-0.5 max-h-[560px] overflow-y-auto">
                {/* "All" — hidden when the search box is active. */}
                {!buildingSearch.trim() && (
                  <button
                    onClick={() => changeBuilding('')}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors text-left',
                      !selectedBuildingId
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700',
                    )}
                  >
                    <Building2 className="w-4 h-4 flex-shrink-0" />
                    All
                  </button>
                )}
                {filteredBuildings.map((building) => (
                  <div
                    key={building.id}
                    onClick={() => changeBuilding(building.id)}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors text-left cursor-pointer group',
                      selectedBuildingId === building.id
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700',
                    )}
                  >
                    <Building2 className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate flex-1">{building.name}</span>
                    <span className="text-xs text-gray-400 group-hover:hidden">{building.unitCount}</span>
                    {canWrite && (
                      <div className={cn(
                        'flex items-center gap-0.5 transition-opacity',
                        selectedBuildingId === building.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                      )}>
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditingBuilding(building); setEditBuildingName(building.name); }}
                          className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                          title="Edit building"
                        >
                          <Edit2 className="w-3 h-3" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeletingBuilding(building); }}
                          className="p-0.5 rounded hover:bg-error-100 dark:hover:bg-error-900/30 text-gray-400 hover:text-error-600 dark:hover:text-error-400"
                          title="Delete building"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </nav>
            </div>
          </div>

          <div className="flex-1 min-w-0 flex flex-col gap-3">
            {/* Mobile-only Building filter — collapses the desktop sidebar
                to a single Select so the device list still narrows on
                small screens. Hidden during Setup Mode (its panel has its
                own picker). */}
            {!isSetupMode && (
              <div className="lg:hidden">
                <Select
                  value={selectedBuildingId || 'all'}
                  onChange={(v) => changeBuilding(v === 'all' ? '' : v)}
                  options={[
                    { value: 'all', label: 'All buildings' },
                    ...buildings.map((b) => ({ value: b.id, label: b.name })),
                  ]}
                />
              </div>
            )}

            {/* Tab strip — hidden during Setup Mode since gateways aren't
                relevant during meter scan-in. Active/inactive is signalled
                by the underline and text colour; the count chip stays
                neutral on both tabs so it doesn't compete. The Grid/List
                view toggle rides the right end of the strip (Devices tab
                only) — same row as the tabs so there's no orphan gap
                above the table. */}
            {!isSetupMode && (
              <div className="flex flex-wrap items-end justify-between gap-x-2 gap-y-2 sm:border-b sm:border-gray-200 sm:dark:border-gray-700">
                {/* Tabs wrap to a second row on narrow viewports rather
                    than horizontally scrolling — scrolling looked off
                    next to the rest of the page. Each button keeps its
                    own border-b-2 so the active-tab indicator still
                    renders on whichever row it lands. */}
                <div className="flex flex-wrap items-end gap-x-0.5 sm:gap-x-1 gap-y-1">
                  <button
                    onClick={() => setActiveTab('units')}
                    className={cn(
                      "inline-flex items-center gap-1.5 px-2 sm:px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
                      activeTab === 'units'
                        ? "border-primary-600 text-primary-600 dark:text-primary-400"
                        : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                    )}
                  >
                    Units
                    <Badge variant="default" size="sm">{units.length}</Badge>
                  </button>
                  <button
                    onClick={() => setActiveTab('devices')}
                    className={cn(
                      "inline-flex items-center gap-1.5 px-2 sm:px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
                      activeTab === 'devices'
                        ? "border-primary-600 text-primary-600 dark:text-primary-400"
                        : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                    )}
                  >
                    Devices
                    <Badge variant="default" size="sm">{mapDevices.length}</Badge>
                  </button>
                  <button
                    onClick={() => setActiveTab('gateways')}
                    className={cn(
                      "inline-flex items-center gap-1.5 px-2 sm:px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
                      activeTab === 'gateways'
                        ? "border-primary-600 text-primary-600 dark:text-primary-400"
                        : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                    )}
                  >
                    Gateways
                    <Badge variant="default" size="sm">{propertyGateways.length}</Badge>
                  </button>
                  <button
                    onClick={() => setActiveTab('map')}
                    className={cn(
                      "inline-flex items-center gap-1.5 px-2 sm:px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
                      activeTab === 'map'
                        ? "border-primary-600 text-primary-600 dark:text-primary-400"
                        : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                    )}
                  >
                    Map
                  </button>
                </div>
                {/* View toggle — Units + Devices share the same setting
                    so flipping on one tab carries to the other. Mobile
                    defaults to grid (resets every visit); the toggle
                    stays visible so the user can opt into list on a
                    narrow screen. */}
                {(activeTab === 'devices' || activeTab === 'units') && (
                  <div
                    role="group"
                    aria-label="View mode"
                    className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-0.5 mb-1.5"
                  >
                    <button
                      type="button"
                      onClick={() => setViewMode('list')}
                      aria-pressed={viewMode === 'list'}
                      title="List view"
                      className={cn(
                        'inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors',
                        viewMode === 'list'
                          ? 'bg-primary-50 text-primary-700 dark:bg-primary-500/15 dark:text-primary-300'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100',
                      )}
                    >
                      <List className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode('grid')}
                      aria-pressed={viewMode === 'grid'}
                      title="Grid view"
                      className={cn(
                        'inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors',
                        viewMode === 'grid'
                          ? 'bg-primary-50 text-primary-700 dark:bg-primary-500/15 dark:text-primary-300'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100',
                      )}
                    >
                      <LayoutGrid className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            )}

            {isSetupMode && connectingTarget && (
              <div className="mb-4">
                <SetupConnectingPanel
                  key={connectingTarget.deviceId}
                  target={connectingTarget}
                  devices={polledDevices}
                  onDismiss={() => setConnectingTarget(null)}
                />
              </div>
            )}

            {isSetupMode ? (
              <SetupModeDeviceGrid
                devices={orderedDevices}
                page={devicesPage}
                perPage={devicesPerPage}
                perPageOptions={DEVICE_PER_PAGE_OPTIONS}
                onPageChange={setDevicesPage}
                onPerPageChange={(n) => setDevicesPerPage(n as typeof DEVICE_PER_PAGE_OPTIONS[number])}
                sortBy={devicesSortBy}
                sortDir={devicesSortDir}
                onSortByChange={setDevicesSortBy}
                onSortDirChange={setDevicesSortDir}
                // After a Configure Pulses save, force-refresh the polled
                // list so the note + button drop off the calibrated tile
                // without waiting for the next poll tick.
                onConfigured={() => { void refetchPolled(); }}
              />
            ) : activeTab === 'gateways' ? (
              <div className="flex-1 flex flex-col">
                <PropertyGatewaysTab
                  propertyId={property.id}
                  onGatewayAssigned={() => {
                    getPropertyGateways(property.id).then(setPropertyGateways).catch(() => { /* keep last list */ });
                  }}
                />
              </div>
            ) : activeTab === 'units' ? (
              <div className="flex-1 flex flex-col">
                <PropertyUnitsTab
                  propertyId={property.id}
                  units={selectedBuildingId ? units.filter((u) => u.buildingId === selectedBuildingId) : units}
                  devices={mapDevices}
                  canWrite={canWrite}
                  viewMode={viewMode}
                  onEditUnit={(u) => setEditingUnit(u)}
                  onDeleteUnit={(u) => setDeletingUnit(u)}
                  onAddUnit={() => openAddUnit()}
                />
              </div>
            ) : activeTab === 'map' ? (
              <div className="flex-1 flex flex-col">
                {mapMarkers.length > 0 || mapCenter ? (
                  <Card variant="elevated" padding="none" className="flex-1">
                    <GoogleMap
                      markers={mapMarkers}
                      center={mapCenter}
                      zoom={16}
                      onMarkerClick={(id) => {
                        if (id.startsWith('property-')) return;
                        if (id.startsWith('gateway-')) navigate(`/gateways/${id.slice('gateway-'.length)}`);
                        else navigate(deviceDetailPath(id, property ? { kind: 'property', id: property.id } : undefined));
                      }}
                      className="h-[60vh] min-h-[480px]"
                    />
                  </Card>
                ) : (
                  <Card variant="elevated" className="flex-1 flex items-center justify-center">
                    <EmptyState
                      icon={<Building2 className="w-8 h-8" />}
                      title="No map data yet"
                      description={canWrite
                        ? 'Add coordinates to this property (or its devices and gateways) to enable the map view.'
                        : 'A manager needs to add coordinates to this property before the map view can be shown.'
                      }
                    />
                  </Card>
                )}
              </div>
            ) : (
              <div className="flex-1 flex flex-col gap-3">

            {/* Filter indicator — rendered when the sidebar has narrowed
                the list to a building. Preserves the "Showing X of Y"
                orientation cue. */}
            {!isSetupMode && (selectedBuildingId || selectedUnitId) && (() => {
              const filterBuilding = buildings.find((b) => b.id === selectedBuildingId);
              const filterUnit = units.find((u) => u.id === selectedUnitId);
              return (
                <div className="flex items-center justify-between gap-3 -mt-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Showing{' '}
                    <span className="font-medium text-gray-900 dark:text-gray-100 tabular-nums">
                      {visibleDevices.length}
                    </span>{' '}
                    of{' '}
                    <span className="tabular-nums">{mapDevices.length}</span>
                    {filterBuilding && (
                      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-primary-50 dark:bg-primary-500/15 text-primary-700 dark:text-primary-300 px-2 py-0.5 text-[11px] font-medium">
                        {filterBuilding.name}
                        {filterUnit && <span className="opacity-70">· {filterUnit.unitNumber}</span>}
                      </span>
                    )}
                  </p>
                  <button
                    type="button"
                    onClick={() => { changeBuilding(''); setSelectedUnitId(''); }}
                    className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline focus:outline-none focus-visible:underline"
                  >
                    Clear filter
                  </button>
                </div>
              );
            })()}

            {devicesLoading && visibleDevices.length === 0 && !isSetupMode ? (
              <LoadingPage />
            ) : visibleDevices.length === 0 ? (
              <Card variant="elevated" className="flex-1 flex items-center justify-center">
                {(() => {
                  // Empty-state copy and actions branch on what the sidebar
                  // tree has selected:
                  //   - Unit selected   → Add Device + Assign Existing Device
                  //   - Building/property → Enter Setup Mode (single hero).
                  //                         Setup Mode IS the device-add flow
                  //                         for buildings + properties; the
                  //                         separate "Add Device" button was
                  //                         redundant per QA feedback.
                  const filterUnit = units.find((u) => u.id === selectedUnitId);
                  const filterBuilding = buildings.find((b) => b.id === selectedBuildingId);

                  if (filterUnit) {
                    return (
                      <EmptyState
                        icon={<DoorOpen className="w-8 h-8" />}
                        title={`No devices in Unit ${filterUnit.unitNumber}`}
                        description="Add a new device or assign one that's already in your inventory."
                        action={
                          canWrite
                            ? {
                                label: 'Add Device',
                                onClick: () => navigate(`/setup?propertyId=${property.id}`),
                                icon: <Plus className="w-3.5 h-3.5" />,
                              }
                            : undefined
                        }
                        secondaryAction={
                          canWrite
                            ? {
                                label: 'Assign Device',
                                onClick: () => setAssignDeviceUnit(filterUnit),
                              }
                            : undefined
                        }
                      />
                    );
                  }

                  // Building-scoped or property-wide empty. Setup Mode is
                  // the canonical entry point for both, so a single hero
                  // action keeps the screen unambiguous — the earlier
                  // "Add Device" secondary just routed into the same
                  // setup flow and read as a duplicate of the primary.
                  return (
                    <EmptyState
                      icon={<Building2 className="w-8 h-8" />}
                      title={filterBuilding ? `No devices in ${filterBuilding.name}` : 'No devices yet'}
                      description={
                        filterBuilding
                          ? 'Setup Mode lets you scan many meters in at once — recommended for a fresh building.'
                          : 'Setup Mode is the fastest way to add meters to this property.'
                      }
                      action={
                        canWrite
                          ? {
                              label: 'Enter Setup Mode',
                              onClick: enterSetupMode,
                              icon: <Zap className="w-3.5 h-3.5" />,
                            }
                          : undefined
                      }
                    />
                  );
                })()}
              </Card>
            ) : (
              <>
                {/* Card grid or list table, controlled by viewMode.
                    Grid = 1/2/3-column responsive card layout with
                    alarm accents (the mobile default — resets every
                    visit). List = tabular PropertyDevicesTable for
                    installers managing many meters on desktop. */}
                {viewMode === 'grid' ? (
                  <div
                    className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3"
                    role="list"
                    aria-label="Devices"
                  >
                    {orderedDevices.map((device) => (
                      <PropertyDeviceCard key={device.id} device={device} />
                    ))}
                  </div>
                ) : (
                  <PropertyDevicesTable
                    devices={orderedDevices}
                    propertyId={property.id}
                    sortBy={devicesSortBy}
                    sortDir={devicesSortDir}
                    onSortByChange={setDevicesSortBy}
                    onSortDirChange={setDevicesSortDir}
                  />
                )}

                {/* Pagination — always visible outside Setup Mode (during
                    Setup Mode the polling hook is the source of truth and
                    pagination doesn't apply). Renders disabled controls at
                    1 page so the affordance is consistent across states. */}
                {!isSetupMode && (
                  <Pagination
                    currentPage={devicesPage}
                    totalPages={devicesTotalPages}
                    totalCount={devicesTotalCount}
                    onPageChange={setDevicesPage}
                    perPage={devicesPerPage}
                    perPageOptions={DEVICE_PER_PAGE_OPTIONS}
                    onPerPageChange={(n) => setDevicesPerPage(n as typeof DEVICE_PER_PAGE_OPTIONS[number])}
                    itemNoun={{ singular: 'device', plural: 'devices' }}
                    extra={
                      <label className="inline-flex items-center gap-2">
                        <span>Sort</span>
                        <div className="w-48">
                          <Select
                            value={`${devicesSortBy}-${devicesSortDir}`}
                            onChange={(val) => {
                              const [next, dir] = val.split('-') as [
                                typeof devicesSortBy,
                                typeof devicesSortDir,
                              ];
                              setDevicesSortBy(next);
                              setDevicesSortDir(dir);
                            }}
                            options={[
                              { value: 'unit-asc', label: 'Unit number (low → high)' },
                              { value: 'unit-desc', label: 'Unit number (high → low)' },
                              { value: 'createdAt-desc', label: 'Recently added' },
                              { value: 'createdAt-asc', label: 'Oldest first' },
                            ]}
                          />
                        </div>
                      </label>
                    }
                  />
                )}
              </>
            )}
              </div>
            )}
          </div>
        </div>
        );
        })()}

      </div>

      {/* Add Building Modal */}
      <Modal
        isOpen={showAddBuilding}
        onClose={() => setShowAddBuilding(false)}
        title="Add Building"
        description={`Add a building to ${property.name}`}
        size="sm"
      >
        <div className="space-y-4">
          <Input
            label="Building Name"
            placeholder="e.g., Building A"
            value={newBuildingName}
            onChange={(e) => setNewBuildingName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddBuilding()}
            autoFocus
          />
          {addBuildingError && <p className="text-sm text-error-600">{addBuildingError}</p>}
          <div className="flex justify-end gap-3 pt-1">
            <Button variant="outline" onClick={() => setShowAddBuilding(false)}>Cancel</Button>
            <Button onClick={handleAddBuilding} disabled={addBuildingLoading || !newBuildingName.trim()}>
              {addBuildingLoading ? 'Adding\u2026' : 'Add Building'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add Unit Modal \u2014 Single / Bulk tabs. Bulk uses BulkUnitsForm
          with prefix + auto-increment + per-row edit; existing units in
          the chosen building drive the collision check. */}
      <Modal
        isOpen={showAddUnit}
        onClose={() => setShowAddUnit(false)}
        title={addUnitMode === 'bulk' ? 'Add Units' : 'Add Unit'}
        size={addUnitMode === 'bulk' ? 'lg' : 'sm'}
      >
        <div className="space-y-4">
          <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700 -mt-2">
            <button
              type="button"
              onClick={() => { setAddUnitMode('single'); setAddUnitError(''); }}
              className={cn(
                'inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium border-b-2 -mb-px transition-colors',
                addUnitMode === 'single'
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100',
              )}
            >
              Single
            </button>
            <button
              type="button"
              onClick={() => { setAddUnitMode('bulk'); setAddUnitError(''); }}
              className={cn(
                'inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium border-b-2 -mb-px transition-colors',
                addUnitMode === 'bulk'
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100',
              )}
            >
              Bulk
            </button>
          </div>

          {!selectedBuildingId && (
            <Select
              label="Building"
              value={newUnitBuildingId}
              onChange={(v) => { setNewUnitBuildingId(v); setBulkRowErrors(undefined); }}
              options={buildings.map((b) => ({ value: b.id, label: b.name }))}
              placeholder="Select a building..."
              required
            />
          )}

          {addUnitMode === 'single' ? (
            <Input
              label="Unit Number"
              placeholder="e.g., 101, 2B"
              value={newUnitNumber}
              onChange={(e) => setNewUnitNumber(e.target.value)}
              required
            />
          ) : (
            <BulkSection
              targetBuildingId={newUnitBuildingId || selectedBuildingId}
              buildings={buildings}
              unitsByBuilding={unitsByBuilding}
              externalErrors={bulkRowErrors}
              onChange={setBulkPreviewUnits}
            />
          )}

          {addUnitError && <p className="text-sm text-error-600">{addUnitError}</p>}
          <div className="flex justify-end gap-3 pt-1">
            <Button variant="outline" onClick={() => setShowAddUnit(false)}>Cancel</Button>
            {addUnitMode === 'single' ? (
              <Button
                onClick={handleAddUnit}
                disabled={addUnitLoading || !newUnitNumber.trim() || (!selectedBuildingId && !newUnitBuildingId)}
              >
                {addUnitLoading ? 'Adding\u2026' : 'Add Unit'}
              </Button>
            ) : (
              <Button
                onClick={handleBulkAddUnits}
                disabled={addUnitLoading || (!selectedBuildingId && !newUnitBuildingId) || bulkPreviewUnits.length === 0}
              >
                {addUnitLoading ? 'Adding\u2026' : 'Add Units'}
              </Button>
            )}
          </div>
        </div>
      </Modal>

      {/* Delete property — destructive cascade. Mike's framing: "delete is
          delete," and devices are hard-deleted server-side. But meter
          readings + ProvisionedMeter rows persist (decoupled, no FK), so
          from a user-data perspective the meters can be re-paired without
          loss — Jordan asked we surface that to readers as "unassigned"
          rather than "deleted," since the meter hardware itself isn't
          gone. Units/buildings are genuinely removed. Type-to-confirm
          guards against stray clicks. */}
      <TypeToConfirmDialog
        isOpen={confirmDeleteProperty}
        title="Delete Property"
        description={
          <>
            <p>
              Permanently delete <strong>{property.name}</strong>? This cannot be undone.
            </p>
            {mapDevices.length > 0 && (
              <>
                <p className="text-gray-700 dark:text-gray-300">The following devices will be unassigned:</p>
                <ul className="list-disc pl-5 space-y-0.5">
                  <li><strong>{mapDevices.length}</strong>{' '}{mapDevices.length === 1 ? 'device' : 'devices'}</li>
                </ul>
              </>
            )}
            {(units.length > 0 || buildings.length > 0) && (
              <>
                <p className="text-gray-700 dark:text-gray-300">The following will also be removed:</p>
                <ul className="list-disc pl-5 space-y-0.5">
                  <li><strong>{units.length}</strong>{' '}{units.length === 1 ? 'unit' : 'units'}</li>
                  <li><strong>{buildings.length}</strong>{' '}{buildings.length === 1 ? 'building' : 'buildings'}</li>
                </ul>
              </>
            )}
          </>
        }
        confirmLabel="Delete Property"
        onConfirm={handleDeleteProperty}
        onCancel={() => setConfirmDeleteProperty(false)}
        isLoading={deletePropertyLoading}
        matchValue={property.name}
        matchLabel="property name"
      />

      {/* Delete building — cascades to its units AND each unit's
          devices (mirrors property-delete and unit-delete). Surface the
          unit + device counts so the user can't miss what's about to
          go. Empty buildings get a minimal dialog. */}
      {(() => {
        const unitsInBuilding = deletingBuilding
          ? (unitsByBuilding[deletingBuilding.id] ?? []).length
          : 0;
        const devicesInBuilding = deletingBuilding
          ? mapDevices.filter((d) => d.buildingId === deletingBuilding.id).length
          : 0;
        const description = deletingBuilding ? (
          unitsInBuilding > 0 || devicesInBuilding > 0 ? (
            <>
              <p>
                Permanently delete <strong>{deletingBuilding.name}</strong>?
                {' '}This cannot be undone.
              </p>
              <p className="text-gray-700 dark:text-gray-300">The following will also be removed:</p>
              <ul className="list-disc pl-5 space-y-0.5">
                <li>
                  <strong>{unitsInBuilding}</strong>{' '}
                  {unitsInBuilding === 1 ? 'unit' : 'units'}
                </li>
                <li>
                  <strong>{devicesInBuilding}</strong>{' '}
                  {devicesInBuilding === 1 ? 'device' : 'devices'}
                  {devicesInBuilding > 0 && ' (each will need to be set up again from scratch)'}
                </li>
              </ul>
            </>
          ) : (
            <p>
              Permanently delete <strong>{deletingBuilding.name}</strong>?
              {' '}This cannot be undone.
            </p>
          )
        ) : null;
        return (
          <ConfirmDialog
            isOpen={!!deletingBuilding}
            title="Delete Building"
            description={description}
            confirmLabel={
              devicesInBuilding > 0
                ? 'Delete Building & Devices'
                : unitsInBuilding > 0
                  ? 'Delete Building & Units'
                  : 'Delete Building'
            }
            onConfirm={handleDeleteBuilding}
            onCancel={() => setDeletingBuilding(null)}
          />
        );
      })()}

      {/* Delete unit — cascades to its devices (matches the property-delete
          flow). Surface the device count when there's something to warn
          about; for empty units the dialog stays minimal so we don't
          create alarm where there's no risk. */}
      {(() => {
        const devicesOnUnit = deletingUnit
          ? mapDevices.filter((d) => d.unitId === deletingUnit.id).length
          : 0;
        const description = deletingUnit ? (
          devicesOnUnit > 0 ? (
            <>
              <p>
                Permanently delete <strong>{deletingUnit.unitNumber}</strong>?
                {' '}This cannot be undone.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                <strong>
                  {devicesOnUnit} {devicesOnUnit === 1 ? 'device' : 'devices'}
                </strong>{' '}
                assigned to this unit will also be deleted. Each will need to
                be set up again from scratch (re-pair via Setup Mode).
              </p>
            </>
          ) : (
            <p>
              Permanently delete <strong>{deletingUnit.unitNumber}</strong>?
              {' '}This cannot be undone.
            </p>
          )
        ) : null;
        return (
          <ConfirmDialog
            isOpen={!!deletingUnit}
            title="Delete Unit"
            description={description}
            confirmLabel={devicesOnUnit > 0 ? 'Delete Unit & Devices' : 'Delete Unit'}
            onConfirm={handleDeleteUnit}
            onCancel={() => setDeletingUnit(null)}
          />
        );
      })()}

      {/* Assign existing unassigned device to the selected unit */}
      <AssignDeviceModal
        isOpen={!!assignDeviceUnit}
        unit={assignDeviceUnit}
        onClose={() => setAssignDeviceUnit(null)}
        onAssigned={refresh}
      />

      {/* Edit unit */}
      {editingUnit && (
        <UnitModal
          unit={editingUnit}
          buildingId={editingUnit.buildingId}
          onClose={() => setEditingUnit(null)}
          onSave={async (data) => {
            await updateUnit(editingUnit.id, data);
            setEditingUnit(null);
            refresh();
          }}
        />
      )}

      {/* Edit building */}
      <Modal
        isOpen={!!editingBuilding}
        onClose={() => setEditingBuilding(null)}
        title="Rename Building"
        size="sm"
      >
        <div className="space-y-4">
          <Input
            label="Building Name"
            value={editBuildingName}
            onChange={(e) => setEditBuildingName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleEditBuilding()}
            autoFocus
          />
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setEditingBuilding(null)}>Cancel</Button>
            <Button onClick={handleEditBuilding} disabled={!editBuildingName.trim()} isLoading={editBuildingLoading}>Save</Button>
          </div>
          {canWrite && (
            <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="danger" className="w-full" onClick={() => { setDeletingBuilding(editingBuilding); setEditingBuilding(null); }}>
                Delete Building
              </Button>
            </div>
          )}
        </div>
      </Modal>

      {showEditProperty && (
        <PropertyModal
          property={property}
          onClose={() => setShowEditProperty(false)}
          onSave={async (data: CreatePropertyRequest) => {
            await updateProperty(property.id, data);
            setShowEditProperty(false);
            refresh();
          }}
          onDelete={canWrite ? () => { setShowEditProperty(false); setConfirmDeleteProperty(true); } : undefined}
        />
      )}

      <ReassignOrganizationModal
        isOpen={showReassignOrg}
        onClose={() => setShowReassignOrg(false)}
        propertyId={property.id}
        propertyName={property.name}
        currentOrganizationId={property.organizationId}
        onReassigned={() => {
          // After reassign, the property leaves the active org's view.
          // Send the user to the Properties list rather than show a 404.
          invalidate(['properties', 'buildings', 'units', 'devices']);
          navigate('/properties');
        }}
      />

    </div>
  );
}

// BulkSection — inline helper that wires the chosen building's existing
// units into BulkUnitsForm and resolves the starting-number default
// (max+1 when units match a consistent `<prefix> <n>` shape, else 1).
// Lives inside this file because it's only used by the Add Unit modal
// and depends on the parent's per-building state.
function BulkSection({
  targetBuildingId,
  buildings,
  unitsByBuilding,
  externalErrors,
  onChange,
}: {
  targetBuildingId: string;
  buildings: Building[];
  unitsByBuilding: Record<string, Unit[]>;
  externalErrors: { rowIndex: number; message: string }[] | undefined;
  onChange: (units: { unitNumber: string; description?: string }[]) => void;
}) {
  if (!targetBuildingId) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Pick a building above to start bulk-adding units.
      </p>
    );
  }
  const targetBuilding = buildings.find((b) => b.id === targetBuildingId);
  const existingUnits = unitsByBuilding[targetBuildingId] ?? [];
  const existingNames = existingUnits.map((u) => u.unitNumber);
  // Resolve the starting-number default by matching every existing
  // unit against `<prefix> <n>`. When all match a shared prefix, the
  // next batch picks up at max+1; otherwise fall back to 1.
  const trailingNumberRe = /^(.*?)(\d+)\s*$/;
  const numericMatches = existingUnits
    .map((u) => trailingNumberRe.exec((u.unitNumber ?? '').trim()))
    .filter((m): m is RegExpExecArray => m !== null);
  const sharedPrefix = numericMatches.length > 0 && numericMatches.every((m) => m[1] === numericMatches[0][1])
    ? numericMatches[0][1]
    : null;
  const maxNumber = numericMatches.length > 0 ? Math.max(...numericMatches.map((m) => parseInt(m[2], 10))) : null;
  const startNumber = sharedPrefix !== null && maxNumber !== null ? maxNumber + 1 : 1;
  const initialPrefix = sharedPrefix !== null ? sharedPrefix : `${targetBuilding?.name ?? ''} `.trimStart();
  return (
    <BulkUnitsForm
      key={targetBuildingId}
      buildingName={targetBuilding?.name ?? ''}
      existingUnitNames={existingNames}
      startNumber={startNumber}
      initialPrefix={initialPrefix}
      externalErrors={externalErrors}
      onChange={onChange}
    />
  );
}
